import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Grid,
  SkeletonShape,
  Stack,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { VIDEOS } from '../constants';
import type { Video } from '../types';
import { VideoPreview } from './video-preview';
import * as styles from './video-directory.css';

interface VideoDirectoryProps {
  videos: Video[];
  isLoading: boolean;
  isError: boolean;
  onSelectVideo: (videoId: string) => void;
  onRetry: () => void;
}

const COLUMNS = 3;

export function VideoDirectory({ videos, isLoading, isError, onSelectVideo, onRetry }: VideoDirectoryProps) {
  const { track } = useAnalytics();

  const handleSelectVideo = (videoId: string) => {
    void track('customer_io_basics', 'video_clicked', { id: videoId });
    onSelectVideo(videoId);
  };

  if (isError) {
    return (
      <EmptyState data-test-video-library-error-loading>
        <EmptyStateTitle icon="warning">Unable to load videos</EmptyStateTitle>
        <EmptyStateDescription>There was a problem loading the videos.</EmptyStateDescription>
        <EmptyStateActions>
          <Button variant="primary" onClick={onRetry}>
            Retry
          </Button>
        </EmptyStateActions>
      </EmptyState>
    );
  }

  return (
    <Grid columns={COLUMNS} gap="300" alignItems="flex-start" justifyContent="space-between">
      {isLoading
        ? VIDEOS.map((_, index) => (
            <Stack key={index} gap="100">
              <SkeletonShape className={styles.placeholder} />
              <Stack gap="100">
                <SkeletonShape w="full" style={{ height: '20px' }} />
                <SkeletonShape style={{ width: '150px', height: '16px' }} />
              </Stack>
            </Stack>
          ))
        : videos.map((video) => (
            <VideoPreview
              key={video.id}
              video={video}
              direction="column"
              onSelect={() => {
                handleSelectVideo(video.id);
              }}
            />
          ))}
    </Grid>
  );
}
